import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import { cn } from '../lib/utils';

const { FiSettings, FiBell, FiMonitor, FiDatabase, FiGlobe, FiDatabase: FiDbIcon, FiVolume2 } = FiIcons;

const SettingsSection = ({ title, icon, children }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800 bg-zinc-900/50">
      <SafeIcon icon={icon} className="text-yellow-400" size={20} />
      <h3 className="text-lg font-black uppercase tracking-widest text-white">{title}</h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const Toggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between py-3">
    <div className="pr-4">
      <div className="font-bold text-white mb-0.5">{label}</div>
      {description && <div className="text-xs font-medium text-zinc-500 leading-relaxed">{description}</div>}
    </div>
    <label className="relative inline-flex items-center cursor-pointer shrink-0">
      <input 
        type="checkbox" 
        className="sr-only peer"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
    </label>
  </div>
);

const Settings = () => {
  const { 
    isTestData, setIsTestData,
    soundEnabled, setSoundEnabled,
    soundVolume, setSoundVolume,
    soundInterval, setSoundInterval,
    keepAwake, setKeepAwake
  } = useSettings();

  const playTestSound = () => {
    if (!soundEnabled) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(soundVolume / 100, ctx.currentTime);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  };

  return (
    <div className="h-full overflow-y-auto scrollbar-hide pb-20 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <h2 className="text-2xl lg:text-3xl font-black uppercase tracking-widest text-white flex items-center gap-3">
          <SafeIcon icon={FiSettings} className="text-yellow-400" />
          Settings
        </h2>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Notifications & Sound */}
        <SettingsSection title="Notifications & Sound" icon={FiBell}>
          <div className="space-y-2 divide-y divide-zinc-800">
            <Toggle 
              label="Sound Notifications" 
              description="Play a beep sound when a new order arrives or is waiting in the queue" 
              checked={soundEnabled} 
              onChange={setSoundEnabled} 
            />

            {soundEnabled && (
              <div className="py-4 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-bold text-white flex items-center gap-2">
                      <SafeIcon icon={FiVolume2} className="text-zinc-400" />
                      Volume ({soundVolume}%)
                    </label>
                    <button 
                      onClick={playTestSound}
                      className="text-xs font-black uppercase text-yellow-400 bg-yellow-400/10 px-3 py-1.5 rounded hover:bg-yellow-400/20 transition-colors"
                    >
                      Test Sound
                    </button>
                  </div>
                  <input 
                    type="range" 
                    min="1" 
                    max="100" 
                    value={soundVolume}
                    onChange={(e) => setSoundVolume(parseInt(e.target.value))}
                    className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                  />
                </div>

                <div className="border-t border-zinc-800 pt-4">
                  <label className="block text-xs font-black uppercase text-zinc-500 mb-2 tracking-wider">
                    Alert Interval (Unresolved Orders)
                  </label>
                  <select 
                    value={soundInterval}
                    onChange={(e) => setSoundInterval(Number(e.target.value))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-shadow appearance-none"
                  >
                    <option value="10">Every 10 seconds</option>
                    <option value="30">Every 30 seconds</option>
                    <option value="60">Every 1 minute</option>
                    <option value="120">Every 2 minutes</option>
                  </select>
                </div>
              </div>
            )}

            <Toggle 
              label="Keep Screen Awake (Wake Lock)" 
              description="Prevents the device from going to sleep or locking automatically. Highly recommended for iOS/iPad displays to ensure continuous background syncing." 
              checked={keepAwake} 
              onChange={setKeepAwake} 
            />
          </div>
        </SettingsSection>

        {/* System & Data */}
        <SettingsSection title="System & Data" icon={FiDatabase}>
          <div className="space-y-4">
            <div className={cn(
              "p-4 rounded-xl border-2 transition-all",
              isTestData ? "bg-yellow-400/5 border-yellow-400/20" : "bg-zinc-800/30 border-transparent"
            )}>
              <Toggle 
                label="Enable Test Mode" 
                description="Use mock data instead of real orders from Tilda" 
                checked={isTestData} 
                onChange={setIsTestData} 
              />
              {isTestData && (
                <div className="mt-2 text-[10px] font-black text-yellow-500 uppercase flex items-center gap-1.5">
                  <SafeIcon icon={FiDbIcon} size={10} />
                  App will now use generated examples for all screens
                </div>
              )}
            </div>

            <div className="pt-4">
              <label className="block text-xs font-black uppercase text-zinc-500 mb-2 tracking-wider">
                Auto-Refresh Interval
              </label>
              <select className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-shadow appearance-none">
                <option value="5">Every 5 seconds</option>
                <option value="10">Every 10 seconds</option>
                <option value="15" selected>Every 15 seconds</option>
                <option value="30">Every 30 seconds</option>
                <option value="60">Every 1 minute</option>
              </select>
            </div>
          </div>
        </SettingsSection>

        {/* General Settings */}
        <SettingsSection title="General" icon={FiGlobe}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-black uppercase text-zinc-500 mb-2 tracking-wider">
                Cinema Name
              </label>
              <input 
                type="text" 
                defaultValue="Heroes Playbar"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-shadow"
              />
            </div>
          </div>
        </SettingsSection>
      </motion.div>
    </div>
  );
};

export default Settings;